import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Polygon from '@arcgis/core/geometry/Polygon';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';
import { WGS84_SPATIAL_REFERENCE } from '../utils/spatialReference';
import { HELENE_CONE_RING } from './historicalHurricanesHeleneData';
import {
  EPA_ERMA_HELENE_SWATH_URL,
  EPA_NPL_SUPERFUND_FEATURE_SERVER_URL,
} from './epaHeleneErmaLayers';
import {
  EPA_GULF_FACILITIES,
  type EpaGulfFacility,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/epaGulfThreatNotification';

const NPL_OUT_FIELDS = [
  'Site_Name',
  'Site_EPA_ID',
  'City',
  'State',
  'Status',
  'Latitude',
  'Longitude',
] as const;

const PAGE_SIZE = 2000;

export type EpaNplSite = EpaGulfFacility & {
  status?: string;
  source: 'npl' | 'fallback';
};

let cachedQuery: Promise<EpaNplSite[]> | null = null;
let cachedSites: EpaNplSite[] | null = null;

function heleneConeFallbackPolygon(): Polygon {
  return new Polygon({
    rings: [HELENE_CONE_RING],
    spatialReference: WGS84_SPATIAL_REFERENCE,
  });
}

function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function asText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function siteFromGraphic(graphic: __esri.Graphic): EpaNplSite | null {
  const attrs = graphic.attributes ?? {};
  const id = asText(attrs.Site_EPA_ID);
  const name = asText(attrs.Site_Name);
  if (!id || !name) return null;

  const geometry = graphic.geometry;
  const lon =
    asNumber(attrs.Longitude) ??
    (geometry?.type === 'point' ? (geometry as __esri.Point).longitude : null);
  const lat =
    asNumber(attrs.Latitude) ??
    (geometry?.type === 'point' ? (geometry as __esri.Point).latitude : null);
  if (lon == null || lat == null) return null;

  return {
    id,
    name,
    city: asText(attrs.City),
    state: asText(attrs.State),
    status: asText(attrs.Status) || undefined,
    lon,
    lat,
    source: 'npl',
  };
}

function fallbackSites(): EpaNplSite[] {
  return EPA_GULF_FACILITIES.map((facility) => ({
    ...facility,
    source: 'fallback' as const,
  }));
}

async function queryHeleneSwathGeometry(): Promise<__esri.GeometryUnion | null> {
  const layer = new FeatureLayer({ url: EPA_ERMA_HELENE_SWATH_URL });
  await layer.load();
  const { features } = await layer.queryFeatures({
    where: '1=1',
    returnGeometry: true,
    outFields: ['*'],
    outSpatialReference: WGS84_SPATIAL_REFERENCE,
  });
  const geometries = features
    .map((feature) => feature.geometry)
    .filter((geometry): geometry is __esri.GeometryUnion => geometry != null);
  if (geometries.length === 0) return null;
  if (geometries.length === 1) return geometries[0];
  return geometryEngine.union(geometries) ?? geometries[0];
}

async function queryNplIntersecting(
  geometry: __esri.GeometryUnion
): Promise<EpaNplSite[]> {
  const layer = new FeatureLayer({
    url: EPA_NPL_SUPERFUND_FEATURE_SERVER_URL,
  });
  await layer.load();

  const sites: EpaNplSite[] = [];
  const seen = new Set<string>();
  let start = 0;

  while (true) {
    const { features, exceededTransferLimit } = await layer.queryFeatures({
      geometry,
      spatialRelationship: 'intersects',
      returnGeometry: true,
      outFields: [...NPL_OUT_FIELDS],
      outSpatialReference: WGS84_SPATIAL_REFERENCE,
      start,
      num: PAGE_SIZE,
    });

    for (const feature of features) {
      const site = siteFromGraphic(feature);
      if (!site || seen.has(site.id)) continue;
      seen.add(site.id);
      sites.push(site);
    }

    if (!exceededTransferLimit || features.length === 0) break;
    start += features.length;
  }

  return sites;
}

async function queryNplSitesInHeleneConeUncached(): Promise<EpaNplSite[]> {
  let cone: __esri.GeometryUnion;
  try {
    cone = (await queryHeleneSwathGeometry()) ?? heleneConeFallbackPolygon();
  } catch (error) {
    console.warn(
      '[EPA NPL] Helene wind-swath query failed; using synthetic cone ring.',
      error
    );
    cone = heleneConeFallbackPolygon();
  }

  try {
    return await queryNplIntersecting(cone);
  } catch (error) {
    console.warn(
      '[EPA NPL] Spatial query against the NPL service failed; using hardcoded EPA Gulf fallback.',
      error
    );
    return fallbackSites();
  }
}

/** Session-cached in-cone NPL Superfund sites (or hardcoded fallback). */
export function queryNplSitesInHeleneCone(): Promise<EpaNplSite[]> {
  if (!cachedQuery) {
    cachedQuery = queryNplSitesInHeleneConeUncached()
      .then((sites) => {
        cachedSites = sites;
        return sites;
      })
      .catch((error) => {
        cachedQuery = null;
        throw error;
      });
  }
  return cachedQuery;
}

export function getCachedNplSitesInHeleneCone(): EpaNplSite[] | null {
  return cachedSites;
}

export function nplSiteDefinitionExpression(sites: EpaNplSite[]): string {
  const ids = sites
    .filter((site) => site.source === 'npl')
    .map((site) => site.id.replace(/'/g, "''"));
  if (ids.length === 0) return '1=0';
  return `Site_EPA_ID IN (${ids.map((id) => `'${id}'`).join(',')})`;
}
